/**
 * El enganche entre una pantalla de tabla y un listado de la API.
 *
 * Todas las pantallas hacen lo mismo: leer el estado de la URL, pedir la
 * primera página, esperar a que la persona deje de teclear, paginar al
 * llegar al final y descartar las respuestas que ya no corresponden a lo
 * que hay escrito. Escribirlo en cada pantalla es garantizar que en alguna
 * se olvide lo último y aparezcan resultados de una búsqueda anterior.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { decodeTableState, encodeTableState, type TableState } from './table-state.js';

export interface ListingPage<Row> {
  items: Row[];
  nextCursor: string | null;
  total: number | null;
}

export interface UseListingOptions {
  /** Ruta de la API, sin prefijo: `/sites`. */
  path: string;
  columnIds: readonly string[];
  /** Filtros que no vienen de la tabla: los botones de filtro rápido. */
  extraParams?: Record<string, string | undefined>;
  /** Parámetros de la URL que NO son estado de tabla y hay que conservar. */
  preserve?: readonly string[];
}

export interface UseListingResult<Row> {
  rows: Row[];
  total: number | null;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  state: TableState;
  setState: (state: TableState) => void;
  params: URLSearchParams;
  setParam: (key: string, value: string | null) => void;
  /**
   * Vuelve a pedir la primera página.
   *
   * Lo necesita cualquier pantalla que además de listar **actúe** sobre lo
   * listado: tras cambiar el estado de un equipo en taller, la fila tiene que
   * reflejarlo sin que la persona recargue el navegador.
   */
  reload: () => void;
}

export function useListing<Row extends { id: string }>(
  options: UseListingOptions,
): UseListingResult<Row> {
  const { api } = useSession();
  const [params, setParams] = useSearchParams();

  const state = useMemo(
    () => decodeTableState(params, options.columnIds),
    [params, options.columnIds],
  );

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Descarta las respuestas de una búsqueda ya superada: al teclear
  // «hotel» salen cinco peticiones y no vuelven en orden.
  const request = useRef(0);

  const extras = JSON.stringify(options.extraParams ?? {});

  const load = useCallback(
    async (from: string | null, append: boolean) => {
      const ticket = ++request.current;
      setLoading(true);

      const query = new URLSearchParams();
      if (state.search.trim()) query.set('search', state.search.trim());
      if (state.sort) {
        query.set('sort', state.sort.column);
        query.set('dir', state.sort.direction);
      }
      for (const [key, value] of Object.entries(state.filters)) {
        if (value.operator === 'eq' && value.values[0]) query.set(key, value.values[0]);
      }
      for (const [key, value] of Object.entries(JSON.parse(extras) as Record<string, string>)) {
        if (value) query.set(key, value);
      }
      if (from) query.set('cursor', from);
      query.set('limit', '100');

      try {
        const page = await api.get<ListingPage<Row>>(`${options.path}?${query.toString()}`);
        if (ticket !== request.current) return;

        setRows((previous) => (append ? [...previous, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
        // El total sólo llega en la primera página. En las siguientes se
        // conserva en lugar de borrarlo.
        if (!append) setTotal(page.total);
        setError(null);
      } catch (caught) {
        if (ticket !== request.current) return;
        setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar los datos.');
      } finally {
        if (ticket === request.current) setLoading(false);
      }
    },
    [api, options.path, state.search, state.sort, state.filters, extras],
  );

  useEffect(() => {
    const timer = setTimeout(() => void load(null, false), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const reload = useCallback(() => {
    void load(null, false);
  }, [load]);

  /** Conserva los parámetros que no son estado de tabla. */
  const withPreserved = useCallback(
    (next: URLSearchParams) => {
      for (const key of options.preserve ?? []) {
        const value = params.get(key);
        if (value !== null) next.set(key, value);
      }
      return next;
    },
    [params, options.preserve],
  );

  const setState = useCallback(
    (next: TableState) => {
      // `replace`: cada letra tecleada no puede ser un paso atrás del
      // navegador.
      setParams(withPreserved(encodeTableState(next)), { replace: true });
    },
    [setParams, withPreserved],
  );

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = withPreserved(encodeTableState(state));
      if (value === null) next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [setParams, withPreserved, state],
  );

  return {
    rows,
    total,
    loading,
    error,
    hasMore,
    loadMore: () => void load(cursor, true),
    state,
    setState,
    params,
    setParam,
    reload,
  };
}
